/**
 * Данные канбана заявок менеджера/админа («Облака» #34).
 *
 * Чтение: GET /v1/admin/requests (manager/owner/front_desk допущены role-
 * exception в cmd/api/main.go).
 *
 * Смена статуса: PATCH /v1/admin/requests/{id}/status (force-status) — бэк
 * НЕ проверяет строгий FSM, поэтому менеджер может перевести заявку в ЛЮБОЙ
 * статус всегда (нет 412 при drag&drop напрямую/назад). Старые точечные
 * start/resolve/cancel больше не нужны для канбана.
 *
 * Server-state только через React Query; после мутации инвалидируем список.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminCreateRequest, forceManagerRequestStatus, listManagerRequests } from '@hsc/api-client';
import type { AdminRequestCreateInput, ManagerRequestsResponse, RequestView } from '@hsc/types';
import type { TargetState } from './kanban-model.js';

const KANBAN_KEY = ['requests', 'kanban'] as const;

/** GET /v1/admin/requests — все заявки отеля для канбана (без фильтра, фильтр на клиенте). */
export function useKanbanRequests() {
  return useQuery<ManagerRequestsResponse>({
    queryKey: KANBAN_KEY,
    // limit повыше — канбан показывает всё; пагинация колонок не делает.
    queryFn: () => listManagerRequests({ limit: 200 }),
    staleTime: 10_000,
  });
}

/**
 * Создание заявки персоналом/менеджером («+ Заявка»). POST /v1/admin/requests.
 * При успехе инвалидирует канбан-список — новая заявка появляется в «Новые».
 */
export function useCreateManagerRequest() {
  const qc = useQueryClient();
  return useMutation<RequestView, Error, AdminRequestCreateInput>({
    mutationFn: (input) => adminCreateRequest(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KANBAN_KEY });
      void qc.invalidateQueries({ queryKey: ['requests', 'admin'] });
    },
  });
}

interface MoveInput {
  id: string;
  /** Целевой статус заявки (force-status в любой статус). */
  target: TargetState;
}

/**
 * Мутация смены статуса (drag&drop или кнопки действий). Форсит статус через
 * PATCH /v1/admin/requests/{id}/status. При успехе инвалидирует канбан-список,
 * чтобы карточка переехала в целевую колонку из ответа бэка.
 */
export function useMoveRequest() {
  const qc = useQueryClient();
  return useMutation<RequestView, Error, MoveInput>({
    mutationFn: (input) => forceManagerRequestStatus(input.id, input.target),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KANBAN_KEY });
      void qc.invalidateQueries({ queryKey: ['requests', 'admin'] });
    },
  });
}
